import { useEffect, useMemo, useState } from "react";
import {
  FiSearch,
  FiCalendar,
  FiChevronDown,
  FiEye,
  FiUpload,
  FiX,
  FiUploadCloud,
  FiRefreshCw,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { FaPlus, FaXRay, FaFlask, FaTint } from "react-icons/fa";
import { GiMicroscope, GiBodyBalance } from "react-icons/gi";
import { MdOutlineMonitorHeart, MdOutlineWaves } from "react-icons/md";
import { message, Spin } from "antd";
import IPDOrderService from "../../../../Services/IPDOrderService";
import LabOrderService from "../../../../Services/LabOrderService";
import UploadResultModal from "./UploadResultModal";

const RAD_TYPE_ICON = {
  "X-Ray": (
    <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
      <FaXRay className="text-blue-500 text-[14px]" />
    </div>
  ),
  "X-Ray Knee": (
    <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
      <FaXRay className="text-violet-500 text-[14px]" />
    </div>
  ),
  MRI: (
    <div className="w-8 h-8 rounded-lg bg-pink-50 flex items-center justify-center">
      <MdOutlineMonitorHeart className="text-pink-500 text-[14px]" />
    </div>
  ),
  Ultrasound: (
    <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center">
      <MdOutlineWaves className="text-teal-500 text-[14px]" />
    </div>
  ),
  DEFAULT_RAD: (
    <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
      <FaXRay className="text-blue-500 text-[14px]" />
    </div>
  ),
};

const PATH_TYPE_ICON = {
  Blood: (
    <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
      <FaTint className="text-red-500 text-[14px]" />
    </div>
  ),
  Urine: (
    <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
      <GiBodyBalance className="text-amber-500 text-[14px]" />
    </div>
  ),
  DEFAULT_PATH: (
    <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
      <GiMicroscope className="text-green-600 text-[14px]" />
    </div>
  ),
};

const getRadIcon = (test = "") => {
  const t = String(test).toLowerCase();
  if (t.includes("x-ray knee") || t.includes("knee"))
    return RAD_TYPE_ICON["X-Ray Knee"];
  if (t.includes("x-ray") || t.includes("chest") || t.includes("xray"))
    return RAD_TYPE_ICON["X-Ray"];
  if (t.includes("mri") || t.includes("scan") || t.includes("ct"))
    return RAD_TYPE_ICON["MRI"];
  if (t.includes("usg") || t.includes("ultrasound") || t.includes("echo"))
    return RAD_TYPE_ICON["Ultrasound"];
  return RAD_TYPE_ICON["DEFAULT_RAD"];
};

const getPathIcon = (nameOrSample = "") => {
  const s = String(nameOrSample).toLowerCase();
  if (
    s.includes("blood") ||
    s.includes("cbc") ||
    s.includes("serum") ||
    s.includes("sugar") ||
    s.includes("lft") ||
    s.includes("kft")
  ) {
    return PATH_TYPE_ICON["Blood"];
  }
  if (s.includes("urine")) return PATH_TYPE_ICON["Urine"];
  return PATH_TYPE_ICON["DEFAULT_PATH"];
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const d = new Date(String(dateStr).replace(" ", "T"));
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
};

const formatDateOnly = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const d = new Date(String(dateStr).replace(" ", "T"));
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

export default function Radiology({ patientId, admissionId, patientDetails }) {
  const [filterType, setFilterType] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rawOrders, setRawOrders] = useState([]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedUploadRow, setSelectedUploadRow] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewModalRow, setViewModalRow] = useState(null);

  const handleOpenUpload = (row) => {
    setSelectedUploadRow(row);
    setUploadModalOpen(true);
  };

  const handleCloseUpload = () => {
    setUploadModalOpen(false);
    setSelectedUploadRow(null);
  };

  const getRadPathList = async () => {
    setLoading(true);
    try {
      const payload = {
        diagnosticType: "RADIOLOGY,PATHOLOGY",
        ...(patientId ? { patientId } : {}),
      };

      const result = await LabOrderService.LabReportUploadGet(payload);
      console.log(result?.data, "result?.data");

      const data = result?.data || [];
      setRawOrders(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching Rad/Path orders:", error);
      message.error("Failed to load Radiology / Pathology records");
      setRawOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getRadPathList();
  }, [patientId]);

  const allParsedRows = useMemo(() => {
    const rows = [];
    rawOrders.forEach((hdr) => {
      const details =
        Array.isArray(hdr?.patientIpdOrderDtl) &&
        hdr.patientIpdOrderDtl.length > 0
          ? hdr.patientIpdOrderDtl
          : [null];
      details.forEach((dtl, idx) => {
        const orderType = (
          dtl?.orderType ||
          hdr?.orderType ||
          hdr?.diagnosticType ||
          ""
        ).toUpperCase();
        const isRadiology =
          orderType === "RADIOLOGY" ||
          orderType.includes("RADIO") ||
          orderType.includes("X-RAY") ||
          orderType.includes("XRAY") ||
          orderType.includes("SCAN") ||
          orderType.includes("MRI") ||
          orderType.includes("CT") ||
          orderType.includes("USG") ||
          orderType.includes("ULTRASOUND");

        const isCompleted =
          hdr.status === 1 ||
          hdr?.isLabReportUploaded === "YES" ||
          Boolean(hdr?.resultString) ||
          Boolean(hdr?.resultDocumentName) ||
          dtl?.status === 1 ||
          Boolean(dtl?.resultDocumentName);

        rows.push({
          uniqueKey: `${hdr.id || hdr.orderId || Math.random()}-${dtl?.id || idx}`,
          hdrId: hdr.id,
          orderId: hdr.orderId || hdr.id || "-",
          orderType: isRadiology ? "RADIOLOGY" : "PATHOLOGY",
          rawOrderType: orderType,
          testName:
            dtl?.orderName ||
            hdr.labTestName ||
            hdr.orderName ||
            hdr.clinicalNotes ||
            hdr.orderType ||
            "Rad/Path Order",
          serviceCode: dtl?.serviceCode || hdr.serviceCode || "-",
          bodyPartOrSample:
            dtl?.uom ||
            dtl?.serviceCode ||
            (isRadiology ? "Radiology Study" : "Lab Sample"),
          orderDate:
            hdr.uploadedDateTime || hdr.orderDate || hdr.createdDate || "-",
          reportDate:
            dtl?.modifiedDate ||
            hdr.uploadedDateTime ||
            hdr.modifiedDate ||
            hdr.orderDate ||
            "-",
          orderedBy:
            hdr.orderedBy || hdr.doctor || hdr.createdBy || "Attending Doctor",
          priority: hdr.priority || "ROUTINE",
          instructions: dtl?.instructions || hdr.clinicalNotes || "-",
          clinicalNotes: hdr.clinicalNotes || "-",
          status: isCompleted ? "Completed" : "Pending",
          documentUrl:
            dtl?.resultDocumentName || hdr?.resultDocumentName || null,
          rawHeader: hdr,
          rawDetail: dtl,
        });
      });
    });
    return rows;
  }, [rawOrders]);

  const filteredRows = useMemo(() => {
    return allParsedRows.filter((item) => {
      if (statusFilter !== "All" && item.status !== statusFilter) {
        return false;
      }

      if (dateFilter) {
        const itemDate = formatDateOnly(item.orderDate);
        const selDate = formatDateOnly(dateFilter);
        if (
          itemDate !== selDate &&
          !String(item.orderDate).includes(dateFilter)
        ) {
          return false;
        }
      }

      if (searchTerm.trim()) {
        const query = searchTerm.toLowerCase();
        const matchesName = item.testName?.toLowerCase().includes(query);
        const matchesOrderId = item.orderId?.toLowerCase().includes(query);
        const matchesDoctor = item.orderedBy?.toLowerCase().includes(query);
        const matchesCode = item.serviceCode?.toLowerCase().includes(query);
        const matchesNotes = item.clinicalNotes?.toLowerCase().includes(query);
        if (
          !matchesName &&
          !matchesOrderId &&
          !matchesDoctor &&
          !matchesCode &&
          !matchesNotes
        ) {
          return false;
        }
      }

      return true;
    });
  }, [allParsedRows, statusFilter, dateFilter, searchTerm]);

  const radList = useMemo(() => {
    return filteredRows.filter((r) => r.orderType === "RADIOLOGY");
  }, [filteredRows]);

  const pathList = useMemo(() => {
    return filteredRows.filter((r) => r.orderType === "PATHOLOGY");
  }, [filteredRows]);

  const displayRad = filterType === "Pathology" ? [] : radList;
  const displayPath = filterType === "Radiology" ? [] : pathList;
  const totalCount = displayRad.length + displayPath.length;

  const handleView = (row) => {
    const file = row.documentUrl;
    if (!file) {
      message.info(
        `Details: ${row.testName} (${row.orderId}) - ${row.instructions}`,
      );
      return;
    }
    setViewModalRow(row);
    setViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setViewModalRow(null);
  };

  const getDocumentFullUrl = (file) => {
    if (!file) return "";
    if (file.startsWith("http://") || file.startsWith("https://")) return file;
    return (process.env.REACT_APP_IMAGE_URL || "") + file;
  };

  return (
    <div className="flex flex-col gap-5 py-5">
      <div className="flex items-center justify-between"></div>

      <div className="bg-white border border-[#e9edf5] rounded-[18px] p-4 flex flex-wrap items-end gap-4 shadow-sm">
        <div className="flex flex-col gap-1">
          <div className="flex border border-[#e5e7eb] rounded-lg overflow-hidden">
            {[
              { label: "All", count: allParsedRows.length },
              {
                label: "Radiology",
                count: allParsedRows.filter((r) => r.orderType === "RADIOLOGY")
                  .length,
              },
              {
                label: "Pathology",
                count: allParsedRows.filter((r) => r.orderType === "PATHOLOGY")
                  .length,
              },
            ].map((t) => (
              <button
                key={t.label}
                onClick={() => setFilterType(t.label)}
                className={`px-4 py-1.5 text-[12px] font-semibold transition-colors flex items-center gap-1.5 ${
                  filterType === t.label
                    ? "bg-[#008f9f] text-white"
                    : "bg-white text-[#374151] hover:bg-gray-50"
                }`}
              >
                <span>{t.label}</span>
                {/* <span
                  className={`text-[10px] px-1.5 py-0.2 rounded-full ${
                    filterType === t.label
                      ? "bg-white/20 text-white"
                      : "bg-gray-100 text-[#6b7280]"
                  }`}
                >
                  {t.count}
                </span> */}
              </button>
            ))}
          </div>
        </div>

        {/* Date Filter */}
        <div className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold text-[#6b7280]">
            Order Date
          </span>
          <div className="flex items-center gap-2 border border-[#e5e7eb] rounded-lg px-3 h-9 bg-white min-w-[180px]">
            <FiCalendar className="text-[#6b7280]" />
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="bg-transparent outline-none text-[12px] text-[#374151] w-full font-medium"
            />
            {dateFilter && (
              <button
                onClick={() => setDateFilter("")}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="text-[12px]" />
              </button>
            )}
          </div>
        </div>

        {/* Report Status */}
        <div className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold text-[#6b7280]">
            Status
          </span>
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 px-3 pr-8 border border-[#e5e7eb] rounded-lg text-[12px] text-[#374151] outline-none appearance-none focus:border-[#008f9f] bg-white cursor-pointer"
            >
              <option value="All">All Statuses</option>
              <option value="Completed">Completed</option>
              <option value="Pending">Pending</option>
            </select>
            <FiChevronDown className="pointer-events-none absolute right-2 top-2.5 text-[#6b7280] text-[12px]" />
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#e9edf5] rounded-[18px] overflow-hidden shadow-sm">
        {loading ? (
          <div className="py-20 flex flex-col items-center justify-center gap-3">
            <Spin size="large" />
            <p className="text-[13px] text-[#6b7280]">
              Loading Rad/Path orders...
            </p>
          </div>
        ) : (
          <>
            {filterType !== "Pathology" && displayRad.length > 0 && (
              <div>
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#edf1f7] bg-blue-50/40">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-blue-100/80 flex items-center justify-center shadow-xs">
                      <FaXRay className="text-blue-600 text-[14px]" />
                    </div>
                    <div>
                      <h3 className="text-[13px] font-bold text-[#111827]">
                        Radiology Orders & Reports
                      </h3>
                    </div>
                  </div>
                  <span className="text-[11px] font-semibold bg-blue-100 text-blue-700 px-2.5 py-0.5 rounded-full">
                    {displayRad.length} Records
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-[#edf1f7] bg-[#fafbff]">
                        {[
                          "Order / Report ID",
                          "Test / Procedure",
                          "Code / UOM",
                          "Order Date",
                          "Instructions / Notes",
                          "Ordered By",
                          "Priority",
                          "Actions",
                        ].map((h) => (
                          <th
                            key={h}
                            className="py-3 px-4 text-[11px] font-bold text-[#6b7280] whitespace-nowrap"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {displayRad.map((r) => (
                        <tr
                          key={r.uniqueKey}
                          className="border-b border-[#edf1f7] last:border-0 hover:bg-[#f8fafc] transition-colors"
                        >
                          <td className="py-3 px-4 whitespace-nowrap">
                            <span className="text-[12px] font-bold text-blue-600">
                              {r.orderId}
                            </span>
                            {r.hdrId && (
                              <span className="text-[10px] text-gray-400 block">
                                #{r.hdrId}
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2.5">
                              {getRadIcon(r.testName)}
                              <div>
                                <span className="text-[12px] font-semibold text-[#111827] block">
                                  {r.testName}
                                </span>
                                {r.rawDetail?.quantity && (
                                  <span className="text-[11px] text-[#6b7280]">
                                    Qty: {r.rawDetail.quantity}{" "}
                                    {r.rawDetail.uom || ""}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-[12px] text-[#4b5563] whitespace-nowrap">
                            <span className="px-2 py-0.5 rounded bg-gray-100 text-[#4b5563] text-[11px] font-medium">
                              {r.serviceCode}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-[12px] text-[#4b5563] whitespace-nowrap">
                            {formatDateTime(r.orderDate)}
                          </td>
                          <td
                            className="py-3 px-4 text-[12px] text-[#4b5563] max-w-[200px] truncate"
                            title={r.instructions}
                          >
                            {r.instructions}
                          </td>
                          <td className="py-3 px-4 text-[12px] text-[#374151] whitespace-nowrap font-medium">
                            {r.orderedBy}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap">
                            <span
                              className={`text-[10px] px-2 py-0.5 rounded font-semibold uppercase ${
                                r.priority === "HIGH" ||
                                r.priority === "URGENT" ||
                                r.priority === "EMERGENCY"
                                  ? "bg-red-50 text-red-600 border border-red-200"
                                  : "bg-gray-100 text-gray-700"
                              }`}
                            >
                              {r.priority}
                            </span>
                          </td>

                          <td className="py-3 px-4 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => handleView(r)}
                                title="View Details / Report"
                                className="w-7 h-7 flex items-center justify-center rounded-md border border-[#edf1f7] text-[#008f9f] hover:bg-teal-50 bg-white transition-colors"
                              >
                                <FiEye className="text-[12px]" />
                              </button>
                              <button
                                onClick={() => handleOpenUpload(r)}
                                title="Upload / Update Result Document"
                                className="w-7 h-7 flex items-center justify-center rounded-md border border-[#edf1f7] text-[#008f9f] hover:bg-teal-50 bg-white transition-colors"
                              >
                                <FiUpload className="text-[12px]" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {filterType !== "Radiology" && displayPath.length > 0 && (
              <div
                className={
                  filterType === "All" && displayRad.length > 0
                    ? "border-t border-[#edf1f7]"
                    : ""
                }
              >
                <div className="overflow-x-auto my-4">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-[#edf1f7] bg-[#fafbff]">
                        {[
                          "Order / Report ID",
                          "Test / Procedure",
                          "Code / Type",
                          "Order Date",
                          "Instructions / Notes",
                          "Ordered By",
                          "Priority",
                          "Actions",
                        ].map((h) => (
                          <th
                            key={h}
                            className="py-3 px-4 text-[11px] font-bold text-[#6b7280] whitespace-nowrap"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {displayPath.map((p) => (
                        <tr
                          key={p.uniqueKey}
                          className="border-b border-[#edf1f7] last:border-0 hover:bg-[#f8fafc] transition-colors"
                        >
                          <td className="py-3 px-4 whitespace-nowrap">
                            <span className="text-[12px] font-bold text-emerald-700">
                              {p.orderId}
                            </span>
                            {p.hdrId && (
                              <span className="text-[10px] text-gray-400 block">
                                #{p.hdrId}
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2.5">
                              {getPathIcon(p.testName)}
                              <div>
                                <span className="text-[12px] font-semibold text-[#111827] block">
                                  {p.testName}
                                </span>
                                {p.rawDetail?.quantity && (
                                  <span className="text-[11px] text-[#6b7280]">
                                    Qty: {p.rawDetail.quantity}{" "}
                                    {p.rawDetail.uom || ""}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-[12px] text-[#4b5563] whitespace-nowrap">
                            <span className="px-2 py-0.5 rounded bg-gray-100 text-[#4b5563] text-[11px] font-medium">
                              {p.serviceCode}{" "}
                              {p.rawOrderType ? `(${p.rawOrderType})` : ""}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-[12px] text-[#4b5563] whitespace-nowrap">
                            {formatDateTime(p.orderDate)}
                          </td>
                          <td
                            className="py-3 px-4 text-[12px] text-[#4b5563] max-w-[200px] truncate"
                            title={p.instructions}
                          >
                            {p.instructions}
                          </td>
                          <td className="py-3 px-4 text-[12px] text-[#374151] whitespace-nowrap font-medium">
                            {p.orderedBy}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap">
                            <span
                              className={`text-[10px] px-2 py-0.5 rounded font-semibold uppercase ${
                                p.priority === "HIGH" ||
                                p.priority === "URGENT" ||
                                p.priority === "EMERGENCY"
                                  ? "bg-red-50 text-red-600 border border-red-200"
                                  : "bg-gray-100 text-gray-700"
                              }`}
                            >
                              {p.priority}
                            </span>
                          </td>

                          <td className="py-3 px-4 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => handleView(p)}
                                title="View Details / Report"
                                className="w-7 h-7 flex items-center justify-center rounded-md border border-[#edf1f7] text-[#008f9f] hover:bg-teal-50 bg-white transition-colors"
                              >
                                <FiEye className="text-[12px]" />
                              </button>
                              <button
                                onClick={() => handleOpenUpload(p)}
                                title="Upload / Update Result Document"
                                className="w-7 h-7 flex items-center justify-center rounded-md border border-[#edf1f7] text-[#008f9f] hover:bg-teal-50 bg-white transition-colors"
                              >
                                <FiUpload className="text-[12px]" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {displayRad.length === 0 && displayPath.length === 0 && (
              <div className="py-16 text-center">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                  <FiSearch className="text-gray-400 text-lg" />
                </div>
                <h4 className="text-[14px] font-semibold text-[#374151]">
                  No reports or orders found
                </h4>
                <p className="text-[12px] text-[#9ca3af] mt-1">
                  {searchTerm || dateFilter || statusFilter !== "All"
                    ? "Try adjusting your filters or search terms."
                    : "No Radiology or Pathology records available for this patient."}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      <UploadResultModal
        isOpen={uploadModalOpen}
        onClose={handleCloseUpload}
        onSuccess={getRadPathList}
        selectedItem={selectedUploadRow}
      />

      {/* View Document Modal */}
      {viewModalOpen && viewModalRow && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          onClick={handleCloseViewModal}
        >
          <div
            className="w-full max-w-3xl bg-white rounded-[18px] overflow-hidden shadow-2xl transition-all"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 flex items-center justify-between border-b border-[#edf1f7]">
              <div>
                <h3 className="text-[16px] font-bold text-[#111827]">
                  View Report
                </h3>
                <p className="text-[12px] text-[#6b7280]">
                  {viewModalRow.testName || "Rad/Path Order"} &bull;{" "}
                  <span className="font-mono text-[#008f9f]">
                    {viewModalRow.orderId}
                  </span>
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseViewModal}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-[#e5e7eb] hover:bg-gray-100 text-[#6b7280] transition-colors"
              >
                <FiX className="text-[15px]" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 max-h-[75vh] overflow-y-auto flex items-center justify-center bg-[#fafafa]">
              {(() => {
                const fileUrl = getDocumentFullUrl(viewModalRow.documentUrl);
                const ext = (viewModalRow.documentUrl || "")
                  .split(".")
                  .pop()
                  ?.toLowerCase();
                const isPdf = ext === "pdf";

                if (isPdf) {
                  return (
                    <iframe
                      src={fileUrl}
                      className="w-full rounded-lg border border-[#e2e8f0]"
                      style={{ height: "65vh" }}
                      title="Report PDF"
                    />
                  );
                }
                return (
                  <img
                    src={fileUrl}
                    alt={viewModalRow.testName || "Report"}
                    className="max-w-full max-h-[65vh] object-contain rounded-lg border border-[#e2e8f0] bg-white p-2"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.parentNode.innerHTML =
                        '<p class="text-[13px] text-[#6b7280] py-10">Unable to load document preview.</p>';
                    }}
                  />
                );
              })()}
            </div>

            {/* Footer */}
            <div className="px-6 py-3 border-t border-[#edf1f7] flex justify-end bg-[#fafbff]">
              <button
                type="button"
                onClick={handleCloseViewModal}
                className="px-5 py-2 bg-white border border-[#e5e7eb] rounded-lg text-[13px] font-semibold text-[#374151] hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
